import { todayInTimezone } from '@mm/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../../api/client';
import { AppIcons } from '../../components/AppIcon';
import { accountApps, appNames } from '../../utils/accounts';
import { Button } from '../../components/ui/Button';
import {
  Alert,
  Card,
  DetailList,
  PageHeader,
  Spinner,
  StatusBadge,
} from '../../components/ui/display';
import { Field, Input } from '../../components/ui/inputs';
import { ConfirmDialog } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { DataTable } from '../../tables/DataTable';
import type { AdvertisingPayment, CreditUsage } from '../../types/models';
import { formatDate, formatMoney, formatPercent } from '../../utils/format';
import { useAuth } from '../auth/AuthProvider';
import { Stat } from '../marketing/MarketingPage';
import { PaymentDialog } from './components';

export function ManagerAccountDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [asOf, setAsOf] = useState(todayInTimezone());
  const [paying, setPaying] = useState(false);
  const [deleting, setDeleting] = useState<AdvertisingPayment | null>(null);
  const [page, setPage] = useState(1);

  const usage = useQuery({
    queryKey: ['billing', 'usage', id, asOf],
    queryFn: () => api.get<CreditUsage>(`/mcc-accounts/${id}/credit-usage`, { asOf }),
    placeholderData: keepPreviousData,
  });
  const payments = useQuery({
    queryKey: ['billing', 'payments', id, page],
    queryFn: () =>
      api.list<AdvertisingPayment>('/advertising-payments', {
        managerAccountId: id,
        page,
        pageSize: 20,
      }),
  });
  const remove = useMutation({
    mutationFn: (p: AdvertisingPayment) => api.delete(`/advertising-payments/${p.id}`),
    onSuccess: () => {
      toast.success('Payment deleted');
      setDeleting(null);
      void queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Delete failed'),
  });

  if (usage.isError)
    return (
      <Alert tone="error">
        {usage.error instanceof ApiError ? usage.error.message : 'Failed to load'}
      </Alert>
    );
  const u = usage.data;
  if (!u) return <Spinner />;
  const m = u.managerAccount;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb={
          <Link to="/mcc-accounts" className="text-brand-700 hover:underline">
            MCC & credit lines
          </Link>
        }
        title={m.name}
        description={[m.platform.name, m.accountIdentifier, m.loginEmail]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            <StatusBadge status={m.status} />
            {can('transactions.write') && u.creditLimit && (
              <Button icon={<CreditCard className="size-4" />} onClick={() => setPaying(true)}>
                Record payment
              </Button>
            )}
          </>
        }
      />

      <div className="flex flex-wrap items-end gap-4">
        <Field label="Position as of" htmlFor="as-of">
          <Input
            id="as-of"
            type="date"
            value={asOf}
            max={todayInTimezone()}
            onChange={(e) => e.target.value && setAsOf(e.target.value)}
            className="w-44"
          />
        </Field>
        <p className="pb-2 text-xs text-slate-500">
          Usage counts spend {m.balanceBasis === 'INCLUDING_GST' ? 'including' : 'excluding'} GST.
          {m.openingBalanceDate &&
            ` Tracking since ${formatDate(m.openingBalanceDate)} with ${formatMoney(m.openingUsedAmount)} already used.`}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat label="Credit limit" value={formatMoney(u.creditLimit)} />
        <Stat
          label="Used"
          value={formatMoney(u.used)}
          sub={u.utilizationPercent ? `${formatPercent(u.utilizationPercent)} of limit` : undefined}
        />
        <Stat
          label="Available"
          value={formatMoney(u.available)}
          tone={u.available && Number(u.available) < 0 ? 'loss' : 'profit'}
        />
        <Stat label="Postpaid spend since opening" value={formatMoney(u.totalSpend)} />
        <Stat label="Payments since opening" value={formatMoney(u.totalPayments)} />
      </div>

      <Card
        title="Credit used by account"
        description="Payments settle the oldest spend first; each account's share is its spend that has not been paid off yet."
        bodyClassName="p-0"
      >
        <DataTable
          tableId="manager-account-detail"
          rows={u.accounts}
          rowKey={(a) => a.account.id}
          columns={[
            {
              key: 'account',
              header: 'Account',
              render: (a) => (
                <span className="flex items-center gap-2">
                  <AppIcons apps={accountApps(a.account)} size={22} />
                  <span>
                    <Link
                      to={`/ads-accounts/${a.account.id}`}
                      className="text-slate-900 hover:text-brand-700"
                    >
                      {a.account.accountName}
                    </Link>
                    <span className="block text-xs text-slate-400">
                      {[appNames(a.account), a.account.accountIdentifier, a.account.loginEmail]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                </span>
              ),
            },
            {
              key: 'month',
              header: 'Spend this month',
              align: 'right',
              render: (a) => formatMoney(a.spendThisMonth),
            },
            {
              key: 'since',
              header: 'Spend since opening',
              align: 'right',
              render: (a) => formatMoney(a.spendSinceOpening),
            },
            {
              key: 'out',
              header: 'Credit used',
              align: 'right',
              render: (a) => (
                <span className="font-medium text-slate-900">{formatMoney(a.outstanding)}</span>
              ),
            },
            {
              key: 'share',
              header: 'Share of used',
              align: 'right',
              render: (a) => formatPercent(a.shareOfUsedPercent),
            },
          ]}
        />
        {Number(u.openingOutstanding) > 0 && (
          <p className="border-t border-slate-100 px-4 py-3 text-sm text-slate-600">
            Opening balance still unpaid:{' '}
            <span className="num font-medium">{formatMoney(u.openingOutstanding)}</span> (
            {formatPercent(u.openingOutstandingSharePercent)} of used)
          </p>
        )}
      </Card>

      <Card title="Payments" bodyClassName="p-0">
        <DataTable<AdvertisingPayment>
          tableId="manager-account-detail-2"
          rows={payments.data?.data}
          rowKey={(p) => p.id}
          loading={payments.isLoading}
          meta={payments.data?.meta}
          onPageChange={setPage}
          columns={[
            { key: 'paymentDate', header: 'Date', render: (p) => formatDate(p.paymentDate) },
            {
              key: 'amount',
              header: 'Amount',
              align: 'right',
              render: (p) => formatMoney(p.amount),
            },
            { key: 'reference', header: 'Reference', render: (p) => p.reference ?? '—' },
            { key: 'notes', header: 'Notes', render: (p) => p.notes ?? '—' },
            {
              key: 'by',
              header: 'Recorded by',
              render: (p) => p.createdBy?.name ?? '—',
            },
            ...(can('transactions.write')
              ? [
                  {
                    key: 'del',
                    header: <span className="sr-only">Delete</span>,
                    align: 'right' as const,
                    render: (p: AdvertisingPayment) => (
                      <button
                        type="button"
                        aria-label="Delete payment"
                        className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                        onClick={() => setDeleting(p)}
                      >
                        <Trash2 className="size-4" />
                      </button>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>

      <Card title="Details">
        <DetailList
          items={[
            {
              label: 'GST account',
              value: `${m.gstAccount.tradeName || m.gstAccount.legalName} (${m.gstAccount.gstin})`,
            },
            { label: 'Platform', value: m.platform.name },
            { label: 'Login email', value: m.loginEmail },
            { label: 'MCC ID', value: m.accountIdentifier },
            {
              label: 'Usage basis',
              value: m.balanceBasis === 'INCLUDING_GST' ? 'Including GST' : 'Excluding GST',
            },
            {
              label: 'Opening used',
              value: m.openingBalanceDate
                ? `${formatMoney(m.openingUsedAmount)} on ${formatDate(m.openingBalanceDate)}`
                : '—',
            },
          ]}
        />
      </Card>

      <PaymentDialog
        target={paying ? { kind: 'mcc', id: m.id, name: m.name } : null}
        onClose={() => setPaying(false)}
      />
      <ConfirmDialog
        open={deleting !== null}
        title="Delete this payment?"
        message={
          deleting &&
          `${formatMoney(deleting.amount)} on ${formatDate(deleting.paymentDate)}. Used credit increases by this amount.`
        }
        confirmLabel="Delete"
        loading={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}
